/*
 * "AMBIENT LIFE" PREFERENCES: the choices, their defaults, and the one function that turns whatever the
 * account holds into something safe to use.
 *
 * What it is: while the site is quiet the dashboard is not frozen. A soft glow sits behind the page boxes, a
 * drifting layer moves behind them, small standby lights show on the boxes, a ring listens around the visitor
 * counter, and a faint tint and a greeting follow the hour. Everything recedes when a visitor arrives
 * (ambientLife.ts owns when).
 *
 * WHY EVERYTHING GOES THROUGH sanitizeAmbient():
 *   - an account written before these settings existed has none of them, and a stored value can be anything
 *     (the backend does not run schema validators on an update). Bubble Speed already taught this: its backend
 *     default matched none of its own radio buttons, so nothing was selected. Anything missing or unrecognised
 *     falls back to the default here, in ONE place, rather than in each reader.
 *   - the backend writes the same defaults (skydive_backend/src/UserApi/Constant/ambientSettings.ts). They
 *     are two copies of one fact on purpose: a server default cannot reach a frontend that is reading an
 *     account which was never saved. If you change one, change the other.
 */

export const AMBIENT_GLOWS = ["none", "bio", "moon", "wind"] as const;
export const AMBIENT_GHOSTS = ["none", "rise", "birds", "drift", "kite"] as const;
/** things that move ALONG THE BOTTOM BAR (the footer's top edge is their sea). One at a time; more can be added here in time. */
export const AMBIENT_BARS = ["none", "fisherman", "sailboats"] as const;

export type AmbientGlow = (typeof AMBIENT_GLOWS)[number];
export type AmbientGhost = (typeof AMBIENT_GHOSTS)[number];
export type AmbientBar = (typeof AMBIENT_BARS)[number];

export interface AmbientSettings {
  ambientGlow: AmbientGlow;
  ambientGhost: AmbientGhost;
  ambientBar: AmbientBar;
  ambientLights: boolean;
  ambientDock: boolean;
  ambientCounter: boolean;
  ambientTimeLight: boolean;
  ambientGreeting: boolean;
}

export const AMBIENT_DEFAULTS: AmbientSettings = {
  ambientGlow: "bio",
  ambientGhost: "rise",
  ambientBar: "none",
  ambientLights: true,
  ambientDock: true,
  ambientCounter: true,
  ambientTimeLight: true,
  ambientGreeting: true,
};

export const AMBIENT_KEYS = Object.keys(AMBIENT_DEFAULTS) as (keyof AmbientSettings)[];

const oneOf = <T extends string>(choices: readonly T[], value: unknown, fallback: T): T =>
  typeof value === "string" && (choices as readonly string[]).includes(value) ? (value as T) : fallback;

const flag = (value: unknown, fallback: boolean): boolean => (typeof value === "boolean" ? value : fallback);

/** Whatever the account holds (or nothing at all) -> a complete, valid set of choices. */
export const sanitizeAmbient = (source: any): AmbientSettings => ({
  ambientGlow: oneOf(AMBIENT_GLOWS, source?.ambientGlow, AMBIENT_DEFAULTS.ambientGlow),
  ambientGhost: oneOf(AMBIENT_GHOSTS, source?.ambientGhost, AMBIENT_DEFAULTS.ambientGhost),
  ambientBar: oneOf(AMBIENT_BARS, source?.ambientBar, AMBIENT_DEFAULTS.ambientBar),
  ambientLights: flag(source?.ambientLights, AMBIENT_DEFAULTS.ambientLights),
  ambientDock: flag(source?.ambientDock, AMBIENT_DEFAULTS.ambientDock),
  ambientCounter: flag(source?.ambientCounter, AMBIENT_DEFAULTS.ambientCounter),
  ambientTimeLight: flag(source?.ambientTimeLight, AMBIENT_DEFAULTS.ambientTimeLight),
  ambientGreeting: flag(source?.ambientGreeting, AMBIENT_DEFAULTS.ambientGreeting),
});

export const sameAmbient = (a: AmbientSettings, b: AmbientSettings): boolean =>
  AMBIENT_KEYS.every((key) => a[key] === b[key]);

/* ---------------------------------------------------------------------------------------------------------
 * THE GREETING. Words only; the name is added by the component.
 *
 * Morning 5:00 to 11:59, afternoon 12:00 to 16:59, evening 17:00 to 21:59, and a plain "Hello" overnight (a
 * "Good night" at 11pm reads as a goodbye). The hour is the VIEWER'S own clock, so it is always true without
 * a location or a timezone setting.
 * ------------------------------------------------------------------------------------------------------- */
export type GreetingPart = "morning" | "afternoon" | "evening" | "hello";

export const greetingPartForHour = (hour: number): GreetingPart => {
  const h = ((hour % 24) + 24) % 24;
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 22) return "evening";
  return "hello";
};
